import { compareMoney, isNegativeMoney, isPositiveMoney, moneyFromWire, type Money } from "@/lib/money"
import type { InstallmentPlanWire, PriceBandWire, ProductPriceWire } from "@/lib/types/price"

export interface InstallmentView {
  count: number
  amounts: Money[]
  total: Money
  dueDates: string[]
  interestFree: boolean
  financingMode: InstallmentPlanWire["financing_mode"]
  paymentCadence: InstallmentPlanWire["payment_cadence"]
  downPayment: Money | null
}

export interface PriceBandView {
  min: Money
  max: Money
}

export interface PriceView {
  amount: Money
  compareAt: Money | null
  isDiscounted: boolean
  savingsPercent: number
  savings: Money | null
  freeShipping: boolean
  installment: InstallmentView | null
  comparableBand: PriceBandView | null
}

// A secured plan off the quincena grid bills once a month; regular plans always pay per quincena.
export const paysMonthly = (plan: Partial<Pick<InstallmentView, "financingMode" | "paymentCadence">>) =>
  plan.financingMode === "SECURED" && plan.paymentCadence !== "EVERY_QUINCENA"

const isPayableIn = (total: Money) => (value: Money) => value.currency === total.currency && isPositiveMoney(value)

export const toInstallmentView = (wire: InstallmentPlanWire | null | undefined): InstallmentView | null => {
  if (!wire || wire.installments < 1) return null
  const amounts = wire.amounts.map(moneyFromWire).filter((amount): amount is Money => amount !== null)
  const total = moneyFromWire(wire.total)
  const downPayment = moneyFromWire(wire.down_payment)
  if (!total || !isPositiveMoney(total) || amounts.length !== wire.installments || !amounts.every(isPayableIn(total))) return null
  if (wire.down_payment != null && (!downPayment || downPayment.currency !== total.currency || isNegativeMoney(downPayment))) return null
  return {
    count: wire.installments,
    amounts,
    total,
    dueDates: wire.due_dates,
    interestFree: Number(wire.annual_interest_rate) === 0,
    financingMode: wire.financing_mode,
    paymentCadence: wire.payment_cadence,
    downPayment,
  }
}

const toBandView = (wire: PriceBandWire | null | undefined): PriceBandView | null => {
  if (!wire) return null
  const min = moneyFromWire(wire.min)
  const max = moneyFromWire(wire.max)
  return min && max ? { min, max } : null
}

export const toPriceView = (wire: ProductPriceWire | null | undefined): PriceView | null => {
  if (!wire) return null
  const amount = moneyFromWire(wire.calculated_price)
  if (!amount) return null
  const base = moneyFromWire(wire.base_price)
  const savings = moneyFromWire(wire.discount_amount)
  const isDiscounted = wire.discount_percent > 0 && base !== null && base.currency === amount.currency && compareMoney(base, amount) > 0
  return {
    amount,
    compareAt: isDiscounted ? base : null,
    isDiscounted,
    savingsPercent: isDiscounted ? wire.discount_percent : 0,
    savings: isDiscounted ? savings : null,
    freeShipping: wire.free_shipping,
    installment: toInstallmentView(wire.installment_plan),
    comparableBand: toBandView(wire.comparable_band),
  }
}
